/**
 * The links a view builds (docs/views.md#5-navigation-sections-and-links), in one implementation for the page
 * (src/client/frame/store.ts, src/client/renderer.ts) and the view's server code (src/server/view-object.ts):
 * `coast.link`'s link to share outside Coast, on the start the view's object hands out, `https://coast.ws/c/<chat>/<view>`
 * in a chat and `https://coast.ws/v/<view>` at its home, and where `coast.post`'s link leads. Opening one is the page's
 * (src/client/links.ts).
 */
import { normalLink } from "../links";

const SECTION = /^[a-z][a-z0-9_-]{0,39}$/;

export function strings(value: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (value && typeof value === "object")
    for (const [key, item] of Object.entries(value)) if (item !== undefined && item !== null) out[key] = String(item);
  return out;
}

/**
 * The link to share a section (`"issue"` or `{ section, params }`) or a record (anything with an `id`, at the section
 * that shows it) of this view; null without a start to build on, as in a link view.
 */
export function shareLink(
  base: string | null | undefined,
  target: unknown,
  sectionOf: (id: string) => string | null,
): string | null {
  return base ? `${base}${sectionLink(target, sectionOf)}` : null;
}

/**
 * The section of this view that `target` names, as a link's fragment, `#issue?id=atl-148`: a section by name, a section
 * with its parameters, or a record by the section that shows it; empty for the view's start.
 */
export function sectionLink(target: unknown, sectionOf: (id: string) => string | null): string {
  let section: string | null = null,
    params: Record<string, string> = {};
  if (typeof target === "string" && SECTION.test(target)) section = target;
  else if (target && typeof target === "object") {
    const value = target as Record<string, unknown>;
    if (typeof value.section === "string" && SECTION.test(value.section)) {
      section = value.section;
      params = strings(value.params);
    } else if (typeof value.id === "string") {
      section = sectionOf(value.id);
      params = section ? { id: value.id } : {};
    }
  }
  const query = new URLSearchParams(params).toString();
  return section ? `#${section}${query ? `?${query}` : ""}` : "";
}

/** The record a link names: the section that shows it, and for another view's, read through `uses`, its link. */
export interface LinkedRecord {
  section: string | null;
  link?: string | null;
}

/**
 * Where a post leads, before the view's object makes it the chat's (docs/chats.md#3-timeline-items), for a page's
 * `coast.post` and an action's alike: this view's section or record as `#<section>?<params>`, the record found by
 * `locate`; another view's record, read through `uses`, by its `coast:` link (src/shared/links.ts), which the object
 * keeps as that view's when it is in the same chat; and the view's start, `#`, for a record nothing shows. A string goes
 * as it is, and no link is null.
 */
export function postTarget(target: unknown, locate: (id: string) => LinkedRecord | null): string | null {
  if (target === undefined || target === null) return null;
  if (typeof target === "string") return target;
  const value = target as Record<string, unknown>;
  const found = typeof value.id === "string" && typeof value.section !== "string" ? locate(value.id) : null;
  if (found?.link) {
    const shared = shareLink(
      found.link,
      found.section ? { section: found.section, params: { id: value.id as string } } : null,
      () => null,
    );
    return normalLink(shared, { origins: [found.link] }) ?? shared;
  }
  return sectionLink(target, (id) => locate(id)?.section ?? null) || "#";
}
